<template>
  <div class="claim-list-item">
    <div class="claim-title">
      <div>
        Claim ID {{ claimLocal.id }}
        <small>
          on order
          <a :href="claimLocal.orderItem.order.link" target="_blank">{{ claimLocal.orderId }}</a>
        </small>
      </div>
      <div>
        <div class="claim-flag-toggle" v-if="claimLocal.flagged">
          <button class="claim-flag-on" @click="onClaimFlagged(false)">Flagged</button>
        </div>
        <div class="claim-flag-toggle" v-else>
          <button class="claim-flag-off" @click="onClaimFlagged(true)">Flag</button>
        </div>
        <div class="claim-status">
          {{ CLAIM_STATUS_LABEL_MAP[CLAIM_STATUS[claimLocal.status]] }}
        </div>
      </div>
    </div>
    <div class="claim-content">
      <!-------- left section -------->
      <div class="claim-section">
        <!-- product details -->
        <div class="claim-info">
          <label>Product Details</label>
          <div class="sku-id">
            <a :href="claimLocal.orderItem.product.link" target="_blank">
              {{ claimLocal.orderItem.product.sku }} - {{ claimLocal.orderItem.product.title }}
            </a>
          </div>
          <div>Quantity x{{ claimLocal.quantity }}</div>

          <div v-for="box in claimLocal.productBoxes" :key="box.internalBoxId" class="product-box">
            <div>
              <span class="title">Box ID </span>
              <span class="bold a-black"
                ><a :href="box.link" target="_blank">{{ box.internalBoxId }}</a></span
              >
            </div>
            <div>
              <span class="title">Box Name</span>
              <span class="description">{{ box.internalDescription }}</span>
            </div>
            <div>
              <span class="title">LOT</span>
              <span>{{ getProductBoxLotNo(claimLocal, box.id) }}</span>
            </div>
            <div>
              <span class="title">Cost</span>
              <span>${{ box.cost }} /each</span>
            </div>
            <div>
              <span class="title">Supplier</span>
              <span>{{ getSupplierName(box.supplierId) }}</span>
            </div>
          </div>
        </div>
        <div
          class="claim-info"
          v-if="claimLocal.replacementProductBoxes && claimLocal.replacementProductBoxes.length"
        >
          <replacement-item-list :replacementProductBoxes="claimLocal.replacementProductBoxes" />
        </div>
        <!-- description -->
        <div class="claim-info">
          <label>Description</label>
          <inline-edit-field
            :isEditable="!isSupportRequest && !isPendingClaim"
            @save="onSaveEdit('description')"
            @cancel="onCancelEdit('description')"
            @edit="onFocusInput('descriptionInput')"
          >
            <template v-slot:read>
              {{ claimLocal.description }}
            </template>
            <template v-slot:edit>
              <textarea
                name="description"
                v-model="claimLocal.description"
                ref="descriptionInput"
              />
            </template>
          </inline-edit-field>
        </div>
        <!-- notes -->
        <claim-notes v-if="!isSupportRequest" :claim="claim" @save="onSaveNote" />
      </div>
      <!-------- right section -------->
      <div class="claim-section">
        <!-- resolution -->
        <div class="claim-info">
          <label>Customer Support Information</label>
          <div>
            <span class="title">Order delivered</span>
            <span class="description">{{
              displayDateTime(claimLocal.orderItem.order.deliveryTimestampUtc)
            }}</span>
          </div>
          <div>
            <span class="title">Issue reported</span>
            <span class="description">{{ displayDateTime(claimLocal.reportingTimestampUtc) }}</span>
          </div>
          <div>
            <span class="title">Issue resolved</span>
            <span class="description">{{
              displayDateTime(claimLocal.resolutionTimestampUtc)
            }}</span>
          </div>
          <div>
            <span class="title">Issue type</span>
            <span class="description">{{
              getSupportRequestIssueTypeName(claimLocal.supportRequestIssueTypeId)
            }}</span>
          </div>
          <div>
            <span class="title">Product quality issue</span>
            <span class="description">
              {{
                claimLocal.productQualityIssue === true
                  ? 'Yes'
                  : claimLocal.productQualityIssue === false
                  ? 'No'
                  : ''
              }}
            </span>
          </div>
          <div>
            <span class="title">Resolution</span>
            <span class="description">{{ getResolutionName(claimLocal.resolutionId) }}</span>
          </div>
        </div>
        <div class="claim-info" v-if="!isSupportRequest && !isNewTicket">
          <label>Select Resolution visible by vendors</label>
          <select
            name="resolution"
            :value="claimLocal.publicResolutionId"
            @input="onChangePublicResolutionId"
          >
            <option value=""></option>
            <option
              v-for="option in dropdownOptions.resolutionTypeOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.name }}
            </option>
          </select>
        </div>
        <div class="claim-info" v-if="!isSupportRequest && !isNewTicket">
          <ul>
            <li>
              <label>Resolution type</label>
              <div class="claim-type-toggle">
                <div
                  v-for="claimType of claimTypes"
                  :key="claimType"
                  class="claim-type"
                  :class="{ selected: claimType === claimLocal.claimType }"
                  @click="selectClaimType(claimType)"
                >
                  {{ CLAIM_TYPE_LABEL_MAP[claimType] }}
                </div>
              </div>
            </li>
            <li
              v-if="claimLocal.claimType === CLAIM_TYPE.CREDIT_REQUESTED"
              class="claim-credit-list"
            >
              <div class="claim-credit-item">
                <label>Amount (USD)</label>
                <inline-edit-field
                  @save="onSaveEdit('requestedAmount')"
                  @cancel="onCancelEdit('requestedAmount')"
                  @edit="onFocusInput('requestedAmountInput')"
                >
                  <template v-slot:read>
                    <input
                      type="number"
                      name="requestedAmount"
                      v-model="claimLocal.requestedAmount"
                      readonly
                    />
                  </template>
                  <template v-slot:edit>
                    <input
                      type="number"
                      name="requestedAmount"
                      v-model="claimLocal.requestedAmount"
                      ref="requestedAmountInput"
                    />
                  </template>
                </inline-edit-field>
              </div>
              <div v-if="isPendingClaim || isArchived" class="claim-credit-item">
                <label>Proposed Credit</label>
                <input type="number" :value="claimLocal.proposedAmount" readonly />
                <div class="button-area">
                  <button
                    class="white accept"
                    v-if="isValidProposalAndStatus"
                    v-on:click="onAcceptVendorProposal()"
                  >
                    Accept
                    <span class="tooltip">
                      This claim will be “Completed” after you click Accept</span
                    >
                  </button>
                </div>
              </div>
              <div v-if="isPendingClaim || isArchived" class="claim-credit-item">
                <label>Final Credit</label>
                <inline-edit-field
                  @save="onSaveEdit('finalAmount')"
                  @cancel="onCancelEdit('finalAmount')"
                  @edit="onFocusInput('finalAmountInput')"
                >
                  <template v-slot:read>
                    <input
                      type="number"
                      name="finalAmount"
                      v-model="claimLocal.finalAmount"
                      readonly
                    />
                  </template>
                  <template v-slot:edit>
                    <input
                      type="number"
                      name="finalAmount"
                      v-model="claimLocal.finalAmount"
                      ref="finalAmountInput"
                    />
                  </template>
                </inline-edit-field>
              </div>
            </li>
          </ul>
        </div>
        <!-- images -->
        <div class="claim-info">
          <label>Media</label>
          <div v-if="claimLocal.media.length === 0">
            <p>No media available</p>
          </div>
          <claim-media-gallery
            v-else
            :media="claimLocal.media"
            :isSharable="!isSupportRequest && !isArchived"
            @toggle-share="toggleShareMedia"
          />
        </div>
        <!-- category -->
        <claim-type-selector
          v-if="!isSupportRequest"
          :categoryId="claimLocal.categoryId"
          :typeId="claimLocal.typeId"
          :issueId="claimLocal.issueId"
          :subIssueId="claimLocal.subIssueId"
          @input="onClaimTypeSelected"
        />
      </div>
    </div>

    <!-- actions -->
    <!-- show both buttons in New Ticket -->
    <div class="claim-actions" v-if="isNewTicket">
      <button class="white" @click="onClaim(false)" :disabled="!canClaim">Don't claim</button>
      <button class="wide" @click="onClaim(true)" :disabled="!canClaim">Claim</button>
    </div>
    <!-- show only Don't claim button in Incomplete Open Claim -->
    <div class="claim-actions" v-if="isOpenClaim && claimLocal.status !== CLAIM_STATUS.QA_COMPLETE">
      <button class="white" @click="onClaim(false)" :disabled="!canClaim">Don't claim</button>
    </div>
    <div class="claim-actions" v-if="isOpenClaim && claimLocal.status == CLAIM_STATUS.QA_COMPLETE">
      <button class="white" @click="onClaim(false)">Archive</button>
    </div>
    <div class="claim-actions" v-if="isArchived">
      <button class="white" @click="onReopen" :disabled="!canClaim">Re-open claim</button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, reactive, ref } from 'vue';
import cloneDeep from 'lodash/cloneDeep';

import { IClaim, IClaimKey, IClaimMedia, IClaimNoteOrReply, IClaimTypeSelections } from '@/types';
import { CLAIM_STATUS, CLAIM_STATUS_LABEL_MAP, CLAIM_TYPE, CLAIM_TYPE_LABEL_MAP } from '@/data';
import { useClaims, useTaxonomy } from '@/hooks';
import ClaimMediaGallery from '@/components/ClaimMediaGallery.vue';
import ClaimNotes from '@/components/ClaimNotes.vue';
import InlineEditField from '@/components/InlineEditField.vue';
import ClaimTypeSelector from '@/components/ClaimTypeSelector.vue';
import { formatDisplayDateTime } from '@/utils';
import ReplacementItemList from '@/components/ReplacementItemList.vue';

export default defineComponent({
  components: {
    ClaimMediaGallery,
    ClaimNotes,
    InlineEditField,
    ClaimTypeSelector,
    ReplacementItemList,
  },
  name: 'ClaimListItem',
  props: {
    claim: Object,
  },
  emits: ['update', 'save-note'],
  setup(props, { emit }) {
    const {
      publicResolutions,
      claimTypes,
      getSupplierName,
      getResolutionName,
      getSupportRequestIssueTypeName,
    } = useTaxonomy();
    const { getProductBoxLotNo } = useClaims();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const claimLocal = reactive<IClaim | Record<string, any>>({
      id: null,
      ...cloneDeep(props.claim),
    }); // copy the claim data to local for write purpose
    const canClaim = computed<boolean>(() =>
      Boolean(
        claimLocal.categoryId && claimLocal.typeId && claimLocal.issueId && claimLocal.subIssueId
      )
    ); // flag, only applies to new tickets
    const dropdownOptions = reactive({
      resolutionTypeOptions: publicResolutions,
    });
    // computed statuses match with left sidebar
    const isSupportRequest = computed<boolean>(
      () => claimLocal.status === CLAIM_STATUS.CUSTOMER_CARE_OPEN
    );
    const isNewTicket = computed<boolean>(
      () => claimLocal.status === CLAIM_STATUS.CUSTOMER_CARE_RESOLVED
    );
    const isOpenClaim = computed<boolean>(
      () =>
        claimLocal.status === CLAIM_STATUS.QA_OPEN || claimLocal.status === CLAIM_STATUS.QA_COMPLETE
    );
    const isPendingClaim = computed<boolean>(
      () =>
        claimLocal.status === CLAIM_STATUS.QA_SUBMITTED ||
        claimLocal.status === CLAIM_STATUS.QA_PENDING ||
        claimLocal.status === CLAIM_STATUS.QA_SETTLED
    );
    const isArchived = computed<boolean>(() => claimLocal.status === CLAIM_STATUS.QA_CLOSED);

    const isValidProposalAndStatus = computed<boolean>(
      () => claimLocal.proposedAmount !== null && claimLocal.status === CLAIM_STATUS.QA_PENDING
    );
    // input refs
    const descriptionInput = ref<HTMLElement | null>(null);
    const requestedAmountInput = ref<HTMLElement | null>(null);
    const finalAmountInput = ref<HTMLElement | null>(null);

    // build the payload only contains the necessary fields that will pass to the backend
    const buildClaimPayload = (fields: IClaimKey[]): IClaim => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload: any = { id: claimLocal.id };
      fields.forEach((field: IClaimKey) => {
        payload[field] = claimLocal[field as keyof IClaim];
      });

      return payload;
    };

    // handle status update
    const updateClaimStatus = (status: string) => {
      claimLocal.status = status;
    };

    // handle claim type update
    const updateClaimType = (type: string | null) => {
      claimLocal.claimType = type;
    };

    // update claim by fields
    const updateClaim = (fields: IClaimKey[]) => {
      if (!props.claim?.id) return;

      const payload = buildClaimPayload(fields);

      emit('update', payload);
    };
    const onAcceptVendorProposal = () => {
      claimLocal.finalAmount = claimLocal.proposedAmount;
      updateClaimStatus(CLAIM_STATUS.QA_SETTLED);
      updateClaim(['finalAmount', 'status']);
    };
    const onSaveEdit = (fieldName: IClaimKey) => {
      // return if the input field value is the same as the original
      if (
        props.claim &&
        props.claim[fieldName] &&
        props.claim[fieldName].toString() === claimLocal[fieldName]?.toString()
      )
        return;

      if (fieldName === 'requestedAmount') {
        claimLocal.requestedAmount = claimLocal.requestedAmount
          ? Number(claimLocal.requestedAmount)
          : null;

        // only update status if in is Open Claim
        if (claimLocal.requestedAmount) {
          updateClaimType(CLAIM_TYPE.CREDIT_REQUESTED);
          if (isOpenClaim.value) {
            updateClaimStatus(CLAIM_STATUS.QA_COMPLETE);
          }
        } else {
          updateClaimType(null);
          if (isOpenClaim.value) {
            updateClaimStatus(CLAIM_STATUS.QA_OPEN);
          }
        }

        updateClaim(['claimType', 'requestedAmount', 'status']);
      } else if (fieldName === 'finalAmount') {
        updateClaimStatus(CLAIM_STATUS.QA_SETTLED);
        updateClaim(['finalAmount', 'status']);
      } else {
        updateClaim([fieldName]);
      }
    };

    const onCancelEdit = (fieldName: IClaimKey) => {
      if (!props.claim) return;

      claimLocal[fieldName as keyof IClaim] = props.claim[fieldName];
    };

    const onFocusInput = async (inputRef: string) => {
      await nextTick();

      switch (inputRef) {
        case 'descriptionInput':
          descriptionInput.value?.focus();
          break;
        case 'requestedAmountInput':
          requestedAmountInput.value?.focus();
          break;
        case 'finalAmountInput':
          finalAmountInput.value?.focus();
          break;
        default:
          break;
      }
    };

    const onClaim = (shouldClaim: boolean) => {
      if (shouldClaim) {
        updateClaimStatus(CLAIM_STATUS.QA_OPEN);
      } else {
        updateClaimStatus(CLAIM_STATUS.QA_CLOSED);
      }

      updateClaim(['status', 'categoryId', 'typeId', 'issueId', 'subIssueId']);
    };

    function onReopen() {
      updateClaimStatus(CLAIM_STATUS.QA_OPEN);

      updateClaim(['status', 'categoryId', 'typeId', 'issueId', 'subIssueId']);
    }

    const selectClaimType = (claimType: string) => {
      if (claimType === claimLocal.claimType) {
        updateClaimType(null);
      } else {
        updateClaimType(claimType);
      }

      // if select Request Acknowledgement and is open claim, auto convert to complete
      if (claimType === CLAIM_TYPE.REQUEST_ACKNOWLEDGMENT && isOpenClaim.value) {
        updateClaimStatus(CLAIM_STATUS.QA_COMPLETE);
        updateClaim(['claimType', 'status']);
      }
    };

    const toggleShareMedia = (media: IClaimMedia) => {
      if (!claimLocal.media) return;

      const currentMediaIndex = claimLocal.media.findIndex((m: IClaimMedia) => m.id === media.id);
      claimLocal.media[currentMediaIndex].isPrivate = media.isPrivate;

      updateClaim(['media']);
    };

    const onChangePublicResolutionId = (e: { target: HTMLInputElement }) => {
      const value = e.target.value;

      claimLocal.publicResolutionId = value === '' ? null : Number(value);

      if (claimLocal.publicResolutionId !== props.claim?.publicResolutionId) {
        updateClaim(['publicResolutionId']);
      }
    };

    function onSaveNote(value: IClaimNoteOrReply): void {
      emit('save-note', value, claimLocal.id);
    }

    const onClaimTypeSelected = (value: IClaimTypeSelections) => {
      claimLocal.categoryId = value.categoryId;
      claimLocal.typeId = value.typeId;
      claimLocal.issueId = value.issueId;
      claimLocal.subIssueId = value.subIssueId;
      if (
        (claimLocal.categoryId &&
          claimLocal.typeId &&
          claimLocal.issueId &&
          claimLocal.subIssueId) ||
        (claimLocal.categoryId === null &&
          claimLocal.typeId === null &&
          claimLocal.issueId === null &&
          claimLocal.subIssueId === null)
      ) {
        updateClaim(['categoryId', 'typeId', 'issueId', 'subIssueId']);
      }
    };

    const displayDateTime = (dateTime: number) => formatDisplayDateTime(dateTime, 'MMM Do, YYYY');

    // handle flagged update
    const onClaimFlagged = (flagged: boolean) => {
      claimLocal.flagged = flagged;
      updateClaim(['flagged']);
    };

    return {
      CLAIM_STATUS,
      CLAIM_STATUS_LABEL_MAP,
      CLAIM_TYPE,
      CLAIM_TYPE_LABEL_MAP,
      displayDateTime,
      claimLocal,
      dropdownOptions,
      onClaimTypeSelected,
      getSupplierName,
      getResolutionName,
      getProductBoxLotNo,
      getSupportRequestIssueTypeName,
      onSaveEdit,
      onCancelEdit,
      onClaim,
      onReopen,
      canClaim,
      claimTypes,
      selectClaimType,
      toggleShareMedia,
      descriptionInput,
      requestedAmountInput,
      finalAmountInput,
      onFocusInput,
      isSupportRequest,
      isNewTicket,
      isOpenClaim,
      isPendingClaim,
      isArchived,
      isValidProposalAndStatus,
      onAcceptVendorProposal,
      onChangePublicResolutionId,
      onSaveNote,
      onClaimFlagged,
    };
  },
});
</script>

<style lang="scss" scoped>
.claim-list-item {
  margin-bottom: var(--space-4);
  background-color: var(--white);
  border: 1px solid var(--grey-light);
  border-radius: 4px;
  padding: var(--space-4);

  .bold {
    font-weight: bold;
  }
  .sku-id {
    margin-bottom: -4px;
  }
  .title {
    width: 180px;
    display: inline-block;
    vertical-align: top;
  }
  .description {
    width: calc(100% - 180px);
    display: inline-block;
  }

  .claim-title {
    font-size: var(--font-size-18);
    font-weight: bold;
    color: var(--grey-ultra-dark);
    display: flex;
    justify-content: space-between;
    align-items: center;

    small {
      font-weight: normal;
    }
  }

  .claim-status {
    font-size: var(--font-size-16);
    float: left;
    margin-top: var(--space-3);
    margin-bottom: var(--space-2);
  }

  .claim-flag-toggle {
    margin-top: var(--space-2);
    margin-bottom: var(--space-2);
    float: left;

    & > * {
      margin-right: 20px;
      border: none;

      &:hover {
        background-color: var(--grey-ultra-light);
      }
    }

    .claim-flag-off {
      margin-right: 20px;
      background-color: var(--grey-ultra-light);
      color: var(--blue);
      padding: var(--space-2) var(--space-6);
      border: none;

      &:hover {
        color: var(--coral-dark);
        background-color: var(--grey-ultra-light);
      }
    }

    .claim-flag-on {
      margin-right: 20px;
      background-color: #ff64581a;
      color: var(--coral-dark);
      border: none;

      &:hover {
        color: var(--coral-dark);
        background-color: var(--grey-ultra-light);
      }
    }
  }

  .claim-content {
    display: flex;
    overflow: hidden;
    margin-top: var(--space-4);
    max-height: 2000px;
    transition: all 0.25s ease;

    &.collapse {
      margin-top: 0;
      height: auto;
      max-height: 0;
    }
  }

  .claim-section {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .claim-info {
    margin-bottom: var(--space-4);
    margin-right: var(--space-6);

    label {
      font-weight: bold;
      font-size: var(--font-size-12);
      color: var(--grey-ultra-dark);
      line-height: 1;
    }

    .product-box {
      font-size: 14px;
      color: #666;
      margin-top: 6px;
      padding-left: 16px;
      line-height: 20px;
      border-left: 2px solid #ccc;
    }

    ul {
      li {
        margin-bottom: var(--space-2);
        line-height: var(--line-height-14);
      }
    }
  }

  .claim-actions {
    display: flex;
    justify-content: flex-end;
    margin-left: calc(-1 * var(--space-4));
    margin-right: calc(-1 * var(--space-4));
    overflow: hidden;
    border-top: 1px solid var(--grey-ultra-light);
    padding-top: var(--space-3);
    max-height: 2000px;

    transition: all 0.25s ease;

    &.hide {
      height: auto;
      max-height: 0;
    }
  }

  .button-area {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 2px;

    button {
      float: right;
      margin-left: var(--space-2);
      &.accept {
        font-size: var(--font-size-12);
        padding: var(--space-1);
        max-width: 60px;
        max-height: 18px;
        &:not(:disabled):not(.disabled) {
          color: var(--success-color);
          &:hover {
            color: var(--success-color);
          }
        }
      }
    }
    button .tooltip {
      position: relative;
      display: block;
      visibility: hidden;
      width: 240px;
      font-size: var(--font-size-5);
      color: var(--blue);
      text-align: left;
      border-radius: 9px;
      padding: 1px 0;
    }
    button:hover .tooltip {
      visibility: visible;
    }
  }

  .claim-type-toggle {
    display: flex;
    align-items: center;
    margin-top: var(--space-2);
    margin-bottom: var(--space-2);

    .claim-type {
      padding: var(--space-2);
      margin-right: var(--space-2);
      background-color: var(--grey-ultra-light);
      border-radius: 4px;
      cursor: pointer;

      &.selected {
        background-color: var(--blue);
        color: var(--white);
      }
    }
  }

  .claim-credit-list {
    display: flex;

    .claim-credit-item {
      margin-right: var(--space-2);

      &:last-child {
        margin-right: 0;
      }
    }
  }
}
</style>
